import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import {
  MatPaginator,
  MatPaginatorIntl,
  MatPaginatorModule,
  PageEvent,
} from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { Router } from '@angular/router';
import { finalize, lastValueFrom } from 'rxjs';

import { NotificationService } from '../../../services/notification/notification.service';
import { NotificacaoView } from '../../../models/NotificacaoView';
import { getPaginatorIntl } from '../../../shared/mat-paginator-intl';
import { NotificacoesDetalhes } from '../notificacoes-detalhes/notificacoes-detalhes';

@Component({
  selector: 'app-notificacoes-list',
  imports: [
    CommonModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    FormsModule,
    MatTableModule,
    MatPaginatorModule,
    MatSelectModule,
    MatButtonModule,
    MatSortModule,
    MatProgressSpinnerModule,
  ],
  providers: [
    {
      provide: MatPaginatorIntl,
      useFactory: getPaginatorIntl,
    },
  ],
  templateUrl: './notificacoes-list.component.html',
  styleUrl: './notificacoes-list.component.scss',
})
export class NotificacoesListComponent implements OnInit {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  todasNotificacoes: NotificacaoView[] = [];
  notificacoesList: NotificacaoView[] = [];
  termoBusca: string = '';

  notificacoes = new MatTableDataSource<any>();
  colunasTabela = [
    { id: 'titulo', header: 'Título da notificação', propriedade: 'titulo' },
    { id: 'dataEnvio', header: 'Recebido em', propriedade: 'dataEnvio' },
  ];

  fieldText = '';
  pageSize = 10;
  pageSizeOptions = [5, 10, 15];
  total = 0;
  inicio = 1;
  fim = 10;

  public isLoading = false;

  get displayedColumns(): string[] {
    return this.colunasTabela.map((coluna) => coluna.id);
  }

  constructor(
    private readonly router: Router,
    private dialog: MatDialog,
    private notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    this.notificacoes.filterPredicate = (data: any, filtro: string) => {
      const titulo = data.titulo?.toLowerCase() || '';
      return titulo.includes(filtro);
    };

    this.isLoading = true;
    this.carregarNotificacoes();
  }

  private parseDateString(dateString: string): number {
    if (!dateString || dateString === '-') return 0;

    const parts = dateString.split('/');
    if (parts.length === 3) {
      const [dia, mes, ano] = parts;
      return new Date(+ano, +mes - 1, +dia).getTime();
    }

    // Tentar parse como ISO string se não for formato DD/MM/YYYY
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? 0 : date.getTime();
  }

  ngAfterViewInit(): void {
    this.notificacoes.paginator = this.paginator;
    this.notificacoes.sort = this.sort;

    this.notificacoes.sortingDataAccessor = (item, property) => {
      switch (property) {
        case 'dataEnvio':
          return this.parseDateString(item.dataEnvio);
        case 'titulo':
          return item.titulo?.toLowerCase() || '';
        default:
          return item[property];
      }
    };

    this.paginator.page.subscribe((event) => this.onPageChange(event));
  }

  onPageChange(event: PageEvent) {
    this.pageSize = event.pageSize;
    this.atualizarContagem(event.pageIndex, event.pageSize);
  }

  atualizarContagem(pageIndex: number, pageSize: number) {
    if (this.total === 0) {
      this.inicio = 0;
      this.fim = 0;
      return;
    }

    this.inicio = pageIndex * pageSize + 1;
    this.fim = Math.min((pageIndex + 1) * pageSize, this.total);
  }

  updateFieldText(event: Event) {
    const query = (event.target as HTMLInputElement).value.trim().toLowerCase();
    this.notificacoes.filter = query;

    this.total = this.notificacoes.filteredData.length;
    this.atualizarContagem(0, this.pageSize);
  }

  async carregarNotificacoes() {
    try {
      const observableNotificacoes = this.notificationService
        .getMinhasNotificacoes()
        .pipe(
          finalize(() => {
            this.isLoading = false;
          })
        );

      // Ajuste conforme a estrutura real da sua API
      const response = await lastValueFrom(observableNotificacoes);
      const notificacoesArray = response.notificacoes || response; // Adapte conforme necessário

      this.notificacoes.data = notificacoesArray.map((notificacao: any) => {
        const statusFormatado = notificacao.status
          ? notificacao.status.charAt(0).toUpperCase() +
            notificacao.status.slice(1).toLowerCase()
          : '-';

        return {
          ...notificacao,
          status: statusFormatado,
          dataEnvio: this.formatarData(notificacao.dataEnvio) || '-',
        };
      });

      this.total = this.notificacoes.data.length;
      this.atualizarContagem(0, this.pageSize);
      this.filterNotificacoes();
    } catch (err) {
      console.error('Falha ao buscar as notificações:', err);
      this.isLoading = false;
    }
  }

  filterNotificacoes(filter: string = '') {
    this.notificacoesList = this.todasNotificacoes.filter((notificacao) =>
      notificacao.titulo.toLowerCase().includes(filter)
    );
  }

  formatarValor(notificacao: any, coluna: any): string {
    if (coluna.format) {
      return coluna.format(notificacao[coluna.propriedade]);
    }
    return notificacao[coluna.propriedade] || '';
  }

  private formatarData(dataInput: Date | string): string {
    if (!dataInput) return '';

    const data = new Date(dataInput);
    const dia = String(data.getDate()).padStart(2, '0');
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const ano = data.getFullYear();

    // Formato apenas com data (sem horas)
    return `${dia}/${mes}/${ano}`;
  }

  getStatusClass(status: string): string {
    const statusNormalizado = status?.toLowerCase().replace(/\s/g, '_');
    switch (statusNormalizado) {
      case 'visualizado':
        return 'status-visualizado';
      case 'nao_visualizado':
        return 'status-nao-visualizado';
      case 'erro':
        return 'status-erro';
      default:
        return 'status-default';
    }
  }

  openDetailsModal(notificacaoClicada: any) {
    const jaEstavaVisualizado =
      notificacaoClicada.status?.toLowerCase() === 'visualizado';

    // Obter TODOS os dados filtrados (sem paginação)
    let todosDadosFiltrados =
      this.notificacoes.filteredData || this.notificacoes.data;

    // Aplicar a MESMA ordenação que a tabela está usando
    if (this.sort.active && this.sort.direction) {
      todosDadosFiltrados = this.sortData(todosDadosFiltrados, this.sort);
    }

    const dialogRef = this.dialog.open(NotificacoesDetalhes, {
      width: '600px',
      data: {
        notificacaoClicada: notificacaoClicada,
        notificacoes: todosDadosFiltrados, // Passar TODAS as notificações filtradas
      },
    });

    dialogRef.afterClosed().subscribe(() => {
      this.carregarNotificacoes();
    });

    if (!jaEstavaVisualizado) {
      notificacaoClicada.status = 'visualizado';
      this.notificacoes.data = [...this.notificacoes.data];

      this.notificationService
        .visualizarNotificacao(notificacaoClicada.id)
        .subscribe({
          next: () => {
            this.carregarNotificacoes();
            this.notificationService.handleNotificacaoVisualized();
          },
          error: (err) => {
            console.error('Erro ao marcar notificação como visualizada', err);
          },
        });
    }
  }

  private sortData(data: any[], sort: Sort): any[] {
    if (!sort.active || sort.direction === '') {
      return data;
    }

    return data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';

      // Usa a mesma função de acesso aos dados do sortingDataAccessor
      const valueA = this.notificacoes.sortingDataAccessor(a, sort.active);
      const valueB = this.notificacoes.sortingDataAccessor(b, sort.active);

      return (valueA < valueB ? -1 : 1) * (isAsc ? 1 : -1);
    });
  }
}
